import FieldType from "@/utils/constants/FieldType.ts";
import AGGridColumn from "@/utils/UIConstants/AGGridColumns/AGGridColumn.ts";
import AGGridIDColumn from "@/utils/UIConstants/AGGridColumns/AGGridIDColumn.ts";
import AGGridRefOrGenRefColumn from "@/utils/UIConstants/AGGridColumns/AGGridRefOrGenRefColumn.ts";
import AGGridTranslatorColumn from "@/utils/UIConstants/AGGridColumns/AGGridTranslatorColumn.ts";
import AGGridTextColumn from "@/utils/UIConstants/AGGridColumns/AGGridTextColumn.ts";
import AGGridFormatterBasedColumn from "@/utils/UIConstants/AGGridColumns/AGGridFormatterBasedColumn.ts";
import AGGridLinkColumn from "@/utils/UIConstants/AGGridColumns/AGGridLinkColumn.ts";
import AGGridAttachmentColumn from "@/utils/UIConstants/AGGridColumns/AGGridAttachmentColumn.ts";
import {ColDef} from "ag-grid-community";
import AGGridBigTextColumn from "@/utils/UIConstants/AGGridColumns/AGGridBigTextColumn.ts";
import {ExtraDisplayColumnMethodOptions} from "@/utils/common/ListViewUtils.ts";
import BSCEntities from "@/utils/constants/BSCEntities.ts";
import ConstantViewNames from "@/utils/constants/ConstantViewNames.ts";
import ObjectChecker from "@/utils/common/ObjectChecker.ts";
import CommonFieldIds from "@/utils/constants/CommonFieldIds.ts";
import ClientCache from "@/utils/login/ClientCache.ts";
import AGGridApproveColumn from "@/utils/UIConstants/AGGridColumns/AGGridApproveColumn.ts";
import AGGridDirectTypeFromIdColumn from "@/utils/UIConstants/AGGridColumns/AGGridDirectTypeFromIdColumn.ts";
import AGGridLaunchReportColumn from "@/utils/UIConstants/AGGridColumns/AGGridLaunchReportColumn.ts";
import AGGridHTMLColumn from "@/utils/UIConstants/AGGridColumns/AGGridHTMLColumn.ts";
import AGGridUtils from "@/utils/UIConstants/AGGridUtils.ts";
import ColumnFiltrationFactory from "@/utils/UIConstants/ColumnFiltrationFactory.ts";
import dayjs, {ISO_DATE_FORMATS} from "@/utils/dayjs";
import AGGridPhoneNumberColumn from "@/utils/UIConstants/AGGridColumns/AGGridPhoneNumberColumn.ts";
import AGGridEmailColumn from "@/utils/UIConstants/AGGridColumns/AGGridEmailColumn.ts";
import {AgCellDataTypes} from "@/utils/UIConstants/enums.ts";

export default class AGGridColumnFactory {
  static createColDefForType(column: namaTypes.ListScreenColumn, entityType: string, extraOptions: ExtraDisplayColumnMethodOptions): ColDef {
    let type = column.metaData!.fieldType;
    if (!type)
      return AGGridColumn.defaultColDef(column, entityType);
    let instance = this.createInstanceForType(type, column, entityType, extraOptions);
    if (!instance)
      return AGGridColumn.defaultColDef(column, entityType);
    return instance.createColDef(extraOptions);
  }

  private static createInstanceForType(fieldType: string, column: namaTypes.ListScreenColumn, entityType: string, extraOptions: ExtraDisplayColumnMethodOptions) {
    if (column && column.id === "code")
      return new AGGridIDColumn(column, entityType);
    let layout = extraOptions.layout;
    if (entityType === BSCEntities.ReportDefinition && layout?.id === ConstantViewNames.REPORT_CATALOGUE_SCREEN_NAME && ObjectChecker.isAnyEqualToFirst(
      column.id, CommonFieldIds.ID, CommonFieldIds.CODE)) {
      return new AGGridLaunchReportColumn(column, entityType);
    }
    if (CommonFieldIds.ID === column.id && ClientCache.isSimpleEntity(layout?.id)) {
      return new AGGridDirectTypeFromIdColumn(column, entityType, layout?.id);
    }
    if (entityType === BSCEntities.ApprovalDefinition && CommonFieldIds.APCASE_ID === column.id) {
      if (layout?.id === ConstantViewNames.EMPLOYEE_APPROVALS_SCREEN_NAME) {
        return new AGGridApproveColumn(column, entityType);
      } else if (layout?.id === "ApprovalCase") {
        return new AGGridDirectTypeFromIdColumn(column, entityType, "ApprovalCase");
      }
    }
    if (entityType === BSCEntities.ApprovalDefinition && CommonFieldIds.APCASE_SUMMARY === column.id) {
      if (layout?.id === ConstantViewNames.EMPLOYEE_APPROVALS_SCREEN_NAME) {
        return new AGGridHTMLColumn(column, entityType);
      }
    }
    if (entityType === BSCEntities.ApprovalDefinition && "subject" === column.id) {
      if (layout?.id === ConstantViewNames.EMPLOYEE_READ_NOTIFICATIONS_SCREEN_NAME) {
        return new AGGridHTMLColumn(column, entityType);
      }
    }
    switch (fieldType) {
      case FieldType.ID:
        return new AGGridIDColumn(column, entityType);
      case FieldType.Reference:
      case FieldType.Genericreference:
        return new AGGridRefOrGenRefColumn(column, entityType);
      case FieldType.EntityType:
      case FieldType.Enum:
      case FieldType.FeatureID:
        return new AGGridTranslatorColumn(column, entityType);
      case FieldType.BigText:
        return new AGGridBigTextColumn(column, entityType);
      case FieldType.Text:
        return new AGGridTextColumn(column, entityType);
      case FieldType.Boolean:
      case FieldType.Date:
      case FieldType.DateAndTime:
      case FieldType.Decimal:
      case FieldType.Integer:
      case FieldType.Long:
      case FieldType.Time:
        return new AGGridFormatterBasedColumn(column, entityType);
      case FieldType.Link:
        return new AGGridLinkColumn(column, entityType);
      case FieldType.Binary:
        return new AGGridAttachmentColumn(column, entityType);
      case FieldType.PhoneNumber:
        return new AGGridPhoneNumberColumn(column, entityType);
      case FieldType.Email:
        return new AGGridEmailColumn(column, entityType);
      default:
        return null;
    }
  }

  public static createColDefForCellDataType(columnKey: string, cellDataType: `${AgCellDataTypes}`): ColDef {
    let filterData: any;
    if (ObjectChecker.isAnyEqualToFirst(cellDataType, AgCellDataTypes.DATE, AgCellDataTypes.DATE_TIME, AgCellDataTypes.DATE_STRING, AgCellDataTypes.DATE_TIME_STRING))
      filterData = ColumnFiltrationFactory.fetchDateTimeFilterData();
    const cellClass = (cellDataType === AgCellDataTypes.TEXT || !cellDataType) ? AGGridUtils.excelStringStyleId : undefined;
    const colDef: ColDef = {
      field: columnKey,
      colId: columnKey,
      filter: true,
      ...filterData,
      cellDataType: cellDataType,
      cellClass: cellClass,
      valueGetter: (params) => {
        return params.data?.[columnKey];
      },
      valueFormatter: (params) => {
        const value = params.value;
        if (!value)
          return '';
        if (!cellDataType)
          return value;
        switch (cellDataType) {
          case AgCellDataTypes.DATE:
          case AgCellDataTypes.DATE_STRING:
            const date2 = dayjs(value, ISO_DATE_FORMATS, true);
            return date2.isValid() ? date2.format("DD-MM-YYYY") : value;
          case AgCellDataTypes.DATE_TIME:
          case AgCellDataTypes.DATE_TIME_STRING:
            const dateTime2 = dayjs(value, ISO_DATE_FORMATS, true);
            return dateTime2.isValid() ? dateTime2.format("DD-MM-YYYY HH:mm:ss") : value;
          default:
            return value;
        }
      }
    };
    return colDef;
  }
}
